import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award,
  Briefcase,
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  Search,
  Upload,
  UserCheck,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";
import apiClient from "@/lib/api";
import { CvBuilderProvider, useCvBuilder } from '@/stores/cvBuilderStore';
import CvImportStep from './cv-builder/CvImportStep';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CandidateProfileReview from './CandidateProfileReview';
import { collectCvSkills } from '@/lib/candidateCvProfile';
import { updateCandidatePoolStatus, createApplication } from "@/lib/recruitmentApi";

function CandidateCreateForm({
  isOpen,
  onClose,
  onCandidateCreated,
  initialTab,
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || "upload"); // "manual" | "upload" | "search"

  // Manual & prefilled form state
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    title: "",
    skills: "",
    experience_years: "",
    availability: "Immédiate",
    source: "manuel",
    cv_file_url: "",
  });

  const { parseUploadedCv, isUploading, importProgress, importError, importWarnings } = useCvBuilder();
  const [imported, setImported] = useState(null);
  const [profile, setProfile] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesReviewed, setDuplicatesReviewed] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Search existing state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedExistingCandidate, setSelectedExistingCandidate] = useState(null);
  const [conflictModal, setConflictModal] = useState(null);
  const [linkingDraft, setLinkingDraft] = useState(false);

  // Orientation choice after creation / selection
  // "profile" (standard save) | "need" (link to need) | "pool" (admit to talent pool)
  const [orientation, setOrientation] = useState("profile");

  // Needs list for positioning
  const [availableNeeds, setAvailableNeeds] = useState([]);
  const [selectedNeedId, setSelectedNeedId] = useState("");

  // Pool delegation fields
  const [poolForm, setPoolForm] = useState({
    director_name: "",
    rh_name: "",
    reason: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Fetch active needs when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const fetchNeeds = async () => {
      try {
        const res = await apiClient.get("/recruitment/needs", { params: { status: "open" } });
        setAvailableNeeds(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Fallback or silent
      }
    };
    void fetchNeeds();
  }, [isOpen]);

  // Fetch recent CVs or search existing
  useEffect(() => {
    if (activeTab !== "search") return;

    if (!searchQuery.trim()) {
      setIsSearching(true);
      apiClient
        .get("/recruitment/candidates/recent-cvs")
        .then((res) => {
          setSearchResults(Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => {
          setSearchResults([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await apiClient.get("/recruitment/candidates/search-existing", {
          params: { q: searchQuery.trim() },
        });
        setSearchResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        // Fallback to legacy candidates endpoint if needed
        try {
          const res = await apiClient.get("/recruitment/candidates", {
            params: { search: searchQuery.trim() },
          });
          setSearchResults(Array.isArray(res.data) ? res.data : []);
        } catch {
          setSearchResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [activeTab, searchQuery]);

  const handleSelectCvOrCandidate = async (item) => {
    if (item.type === "candidate" || item.candidate_id) {
      setSelectedExistingCandidate({
        _id: item.candidate_id || item.id,
        id: item.candidate_id || item.id,
        first_name: item.first_name || item.full_name?.split(" ")[0] || "",
        last_name: item.last_name || item.full_name?.split(" ").slice(1).join(" ") || "",
        title: item.title,
        email: item.email,
        pool_status: item.pool_status,
      });
      return;
    }

    // It is an unlinked CV Builder draft
    setLinkingDraft(true);
    try {
      const res = await apiClient.post("/recruitment/candidates/from-cv-draft", {
        cv_draft_id: item.id || item.cv_draft_id,
      });
      if (res.data?.action_required === "CONFIRM_ACTION") {
        setConflictModal({
          draft: item,
          existing_candidate_id: res.data.existing_candidate_id,
          existing_candidate_name: res.data.existing_candidate_name,
        });
        return;
      }
      toast.success("Candidat rattaché au CV Builder avec succès (sans nouvel appel IA).");
      const cand = res.data?.candidate;
      setSelectedExistingCandidate({
        _id: cand?._id || cand?.id,
        id: cand?._id || cand?.id,
        first_name: cand?.first_name,
        last_name: cand?.last_name,
        title: cand?.title,
        email: cand?.email,
        pool_status: cand?.pool_status,
      });
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.action_required === "CONFIRM_ACTION") {
        setConflictModal({
          draft: item,
          existing_candidate_id: err.response.data.existing_candidate_id,
          existing_candidate_name: err.response.data.existing_candidate_name,
        });
      } else {
        toast.error(err.response?.data?.detail || "Impossible de rattacher ce CV au candidat.");
      }
    } finally {
      setLinkingDraft(false);
    }
  };

  const handleResolveConflict = async (replaceExisting, keepAndModify) => {
    if (!conflictModal) return;
    setLinkingDraft(true);
    try {
      const res = await apiClient.post("/recruitment/candidates/from-cv-draft", {
        cv_draft_id: conflictModal.draft.id || conflictModal.draft.cv_draft_id,
        replace_existing: Boolean(replaceExisting),
        keep_and_modify: Boolean(keepAndModify),
        target_candidate_id: conflictModal.existing_candidate_id,
      });
      toast.success(
        replaceExisting
          ? "CV de référence remplacé avec succès."
          : "CV de référence conservé et profil mis à jour."
      );
      const cand = res.data?.candidate;
      setSelectedExistingCandidate({
        _id: cand?._id || cand?.id || conflictModal.existing_candidate_id,
        id: cand?._id || cand?.id || conflictModal.existing_candidate_id,
        first_name: cand?.first_name || conflictModal.existing_candidate_name?.split(" ")[0],
        last_name: cand?.last_name || conflictModal.existing_candidate_name?.split(" ").slice(1).join(" "),
        title: cand?.title,
        email: cand?.email,
        pool_status: cand?.pool_status,
      });
      setConflictModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la résolution du conflit.");
    } finally {
      setLinkingDraft(false);
    }
  };

  if (!isOpen) return null;

  const handleFileUpload = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files ? fileOrEvent.target.files[0] : fileOrEvent;
    if (!file) return;
    setUploadError(null);

    try {
      const parsed = await parseUploadedCv(file);
      if (parsed?.success === false || !parsed?.draft) {
        throw new Error(parsed?.error_message || "Le CV n'a pas pu être analysé.");
      }
      const draft = parsed.draft;
      const candidate = draft.candidate || {};
      const nameParts = String(candidate.full_name || "").trim().split(/\s+/).filter(Boolean);
      setImported(parsed);
      setProfile(draft);
      setDuplicates(null);
      setDuplicatesReviewed(false);
      setForm((prev) => ({
        ...prev,
        first_name: candidate.first_name || nameParts[0] || prev.first_name,
        last_name: candidate.last_name || nameParts.slice(1).join(" ") || prev.last_name,
        email: candidate.email || prev.email,
        phone: candidate.phone || prev.phone,
        title: candidate.title || prev.title,
        skills: collectCvSkills(draft).join(", ") || prev.skills,
        experience_years: candidate.years_of_experience != null
          ? String(candidate.years_of_experience)
          : prev.experience_years,
        source: "cv_upload",
      }));
      toast.success("CV analysé avec succès ! Vérifiez et complétez les informations.");
      setActiveTab("manual");
    } catch (err) {
      setUploadError(err?.response?.data?.detail || err?.message || "Échec de l'analyse automatique du CV. Vous pouvez saisir les informations manuellement.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);

    try {
      if (orientation === "need" && !selectedNeedId) throw new Error("Sélectionnez un besoin client.");
      if (orientation === "pool" && (!poolForm.director_name.trim() || !poolForm.rh_name.trim() || !poolForm.reason.trim())) throw new Error("L’accord Direction, le collaborateur RH et le motif sont obligatoires pour le vivier.");
      let candidateId = null;
      let candidateName = "";

      if (activeTab === "search") {
        if (!selectedExistingCandidate) {
          setSubmitError("Veuillez sélectionner un candidat existant dans la liste.");
          setSubmitting(false);
          return;
        }
        candidateId = selectedExistingCandidate._id || selectedExistingCandidate.id;
        candidateName = `${selectedExistingCandidate.first_name} ${selectedExistingCandidate.last_name}`;
      } else {
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
          setSubmitError("Le prénom, le nom et l'email sont obligatoires.");
          setSubmitting(false);
          return;
        }
        const skillsArray = form.skills
          ? form.skills.split(",").map((s) => s.trim()).filter(Boolean)
          : [];

        if (!duplicatesReviewed) {
          const check = await apiClient.post('/recruitment/candidates/import-check', { first_name:form.first_name.trim(), last_name:form.last_name.trim(), email:form.email.trim(), phone:form.phone.trim() || null });
          if (check.data?.has_duplicates) { setDuplicates(check.data); return; }
        }
        const createRes = await apiClient.post("/recruitment/candidates", {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          title: form.title.trim() || undefined,
          skills: [...new Set([...skillsArray, ...(profile ? collectCvSkills(profile) : [])])],
          experience_years: form.experience_years ? Number(form.experience_years) : undefined,
          availability: form.availability,
          source: form.source || "manuel",
          cv_draft_id: imported?.draft_id || undefined,
          cv_file_url: form.cv_file_url || undefined,
          cv_profile: profile ? { experiences:profile.experiences || [], projects:profile.projects || [], education:profile.education || [], certifications:profile.certifications || [], languages:profile.languages || [] } : undefined,
          status: "active",
        });

        candidateId = createRes.data?._id || createRes.data?.id;
        candidateName = `${form.first_name} ${form.last_name}`;
      }

      if (!candidateId) {
        throw new Error("Impossible de déterminer l'identifiant du candidat.");
      }

      if (orientation === "need" && !selectedNeedId) {
        throw new Error("Sélectionnez un besoin client avant de confirmer le positionnement.");
      }

      if (
        orientation === "pool"
        && (!poolForm.director_name.trim() || !poolForm.rh_name.trim() || !poolForm.reason.trim())
      ) {
        throw new Error("L'accord Direction, le collaborateur RH et le motif sont obligatoires pour le vivier.");
      }

      // Handle orientation choice
      if (orientation === "need" && selectedNeedId) {
        await createApplication({
          candidate_id: candidateId,
          need_id: selectedNeedId,
        });
        toast.success(`${candidateName} positionné avec succès sur la mission !`);
        onClose();
        if (onCandidateCreated) onCandidateCreated(candidateId);
        navigate(`/admin/recrutement/besoins/${selectedNeedId}`);
        return;
      }

      if (orientation === "pool") {
        const fullReason = `Accord Direction : ${poolForm.director_name.trim()} | Saisi par : ${poolForm.rh_name.trim()} | Motif : ${poolForm.reason.trim()}`;
        await updateCandidatePoolStatus(candidateId, "in_pool", fullReason);
        toast.success(`${candidateName} inscrit avec succès au vivier de talents !`);
        onClose();
        if (onCandidateCreated) onCandidateCreated(candidateId);
        navigate(`/admin/recrutement/vivier`);
        return;
      }

      // Default: "profile" (qualification standard)
      toast.success(`Fiche de ${candidateName} enregistrée avec succès.`);
      onClose();
      if (onCandidateCreated) onCandidateCreated(candidateId);
      navigate(`/admin/recrutement/candidats/${candidateId}`);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || err?.message || "Erreur lors de l'enregistrement du candidat.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => {if (!open && !isUploading && !submitting) onClose();}}>
    <DialogContent closeDisabled={isUploading || submitting} data-testid="candidate-create-modal" className="bg-[#101426] text-white p-0 max-w-2xl w-[calc(100%_-_2rem)] border-white/10 rounded-2xl">
      <DialogTitle className="sr-only">Ajouter un candidat</DialogTitle><DialogDescription className="sr-only">Importez un CV ou saisissez une fiche candidat.</DialogDescription>
      <div className="bg-[#101426] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 pr-8 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-blue-400" />
              Nouveau Candidat
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Importez un CV pour pré-remplir la fiche ou saisissez-la manuellement.
            </p>
          </div>
        </div>

        {/* Mode Selector Tabs */}
        <div className="grid grid-cols-3 gap-2 p-1 bg-white/[0.03] border border-white/10 rounded-xl">
          <button
            type="button"
            onClick={() => setActiveTab("upload")}
            className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === "upload"
                ? "bg-blue-600 text-white shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Upload className="h-3.5 w-3.5" />
            Importer un CV
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("manual")}
            className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === "manual"
                ? "bg-blue-600 text-white shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <FileText className="h-3.5 w-3.5" />
            Saisir manuellement
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("search")}
            className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
              activeTab === "search"
                ? "bg-blue-600 text-white shadow-md"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Search className="h-3.5 w-3.5" />
            Rechercher existant
          </button>
        </div>

        {/* TAB 1: CV UPLOAD */}
        {activeTab === "upload" && (
          <div className="space-y-4">
            <CvImportStep
              onFileSelected={handleFileUpload}
              isLoading={isUploading}
              importProgress={importProgress}
              importError={importError}
              importWarnings={importWarnings}
              lastImportResult={imported}
            />

            {uploadError && (
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: MANUAL / PREFILLED FORM */}
        {activeTab === "manual" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300">
                {submitError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Prénom *</label>
                <input
                  type="text"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="create-candidate-first-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Nom *</label>
                <input
                  type="text"
                  required
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="create-candidate-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="create-candidate-email"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="candidate-form-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-300 mb-1">Intitulé du poste / Titre</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="create-candidate-title"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Années d&apos;exp.</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={form.experience_years}
                  onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                  className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  data-testid="candidate-form-exp"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Compétences clés</label>
              <textarea
                rows={3}
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                placeholder="ex: React, Python, FastAPI..."
                className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white resize-y"
                data-testid="candidate-form-skills"
              />
            </div>

            {/* CandidateProfileReview if profile parsed */}
            {profile && <CandidateProfileReview profile={profile} onChange={setProfile} />}

            {duplicates?.has_duplicates && (
              <div role="alert" className="border border-amber-400/40 rounded-xl p-4 text-sm text-amber-200 space-y-3">
                <p>Des candidats similaires existent déjà. Vérifiez leur fiche avant de créer un doublon.</p>
                {(duplicates.matches || []).map((match) => (
                  <a
                    className="block underline"
                    key={match.candidate_id}
                    href={`/admin/recrutement/candidats/${match.candidate_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {match.first_name} {match.last_name} — {match.details || match.confidence}
                  </a>
                ))}
                <label className="flex gap-2 items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={duplicatesReviewed}
                    onChange={(e) => setDuplicatesReviewed(e.target.checked)}
                  />
                  J’ai vérifié : il s’agit d’une autre personne.
                </label>
              </div>
            )}

            {/* Orientation choice */}
            <div className="pt-2 border-t border-white/10 space-y-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                Orientation après création
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setOrientation("profile")}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    orientation === "profile"
                      ? "bg-blue-600/20 border-blue-500 text-white"
                      : "bg-white/[0.02] border-white/10 text-slate-400"
                  }`}
                >
                  <p className="text-xs font-semibold flex items-center gap-1.5">
                    <UserCheck className="h-3.5 w-3.5 text-blue-400" />
                    En qualification
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setOrientation("need")}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    orientation === "need"
                      ? "bg-blue-600/20 border-blue-500 text-white"
                      : "bg-white/[0.02] border-white/10 text-slate-400"
                  }`}
                >
                  <p className="text-xs font-semibold flex items-center gap-1.5">
                    <Briefcase className="h-3.5 w-3.5 text-blue-400" />
                    Positionner sur un besoin
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setOrientation("pool")}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    orientation === "pool"
                      ? "bg-amber-600/20 border-amber-500 text-white"
                      : "bg-white/[0.02] border-white/10 text-slate-400"
                  }`}
                >
                  <p className="text-xs font-semibold flex items-center gap-1.5">
                    <Award className="h-3.5 w-3.5 text-amber-400" />
                    Inscrire au vivier
                  </p>
                </button>
              </div>

              {orientation === "need" && (
                <div className="pt-2">
                  <label className="block text-xs font-medium text-slate-300 mb-1">Sélectionner un besoin ouvert *</label>
                  <select
                    value={selectedNeedId}
                    onChange={(e) => setSelectedNeedId(e.target.value)}
                    required
                    className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                    data-testid="candidate-need-select"
                  >
                    <option value="">-- Choisir une mission client --</option>
                    {availableNeeds.map((n) => (
                      <option key={n._id} value={n._id}>
                        {n.title} {n.client_id ? `(${n.client_id})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {orientation === "pool" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <input
                    type="text"
                    value={poolForm.director_name}
                    onChange={(e) => setPoolForm({ ...poolForm, director_name: e.target.value })}
                    placeholder="Directeur ayant donné son accord *"
                    className="rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  />
                  <input
                    type="text"
                    value={poolForm.rh_name}
                    onChange={(e) => setPoolForm({ ...poolForm, rh_name: e.target.value })}
                    placeholder="Collaborateur RH validant *"
                    className="rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  />
                  <input
                    type="text"
                    value={poolForm.reason}
                    onChange={(e) => setPoolForm({ ...poolForm, reason: e.target.value })}
                    placeholder="Motif d'inscription au vivier *"
                    className="sm:col-span-2 rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-xs font-semibold text-white shadow-md flex items-center gap-2"
                data-testid="create-candidate-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Enregistrer le candidat
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* TAB 3: SEARCH EXISTING */}
        {activeTab === "search" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, email ou poste..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl bg-slate-900 border border-white/10 pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {isSearching && (
              <div className="flex items-center justify-center py-6 text-slate-400 text-xs">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Recherche dans la bibliothèque unique...
              </div>
            )}

            {!isSearching && searchQuery.trim() && searchResults.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-xs">
                Aucun profil ou CV trouvé pour &quot;{searchQuery}&quot;
              </div>
            )}

            {!isSearching && !searchQuery.trim() && searchResults.length > 0 && (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Derniers CV de la bibliothèque (3-4 récents)
              </p>
            )}

            {!isSearching && searchResults.length > 0 && (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                {searchResults.map((cand) => {
                  const candId = cand.candidate_id || cand._id || cand.id;
                  const isSelected =
                    selectedExistingCandidate &&
                    (selectedExistingCandidate._id === candId ||
                      selectedExistingCandidate.id === candId);
                  const isDraft = cand.type === "draft" || cand.source === "cv_builder_draft";

                  return (
                    <button
                      key={cand.id || cand._id}
                      type="button"
                      disabled={linkingDraft}
                      onClick={() => handleSelectCvOrCandidate(cand)}
                      className={`w-full p-3 rounded-xl border text-left transition-all flex items-center justify-between ${
                        isSelected
                          ? "bg-blue-600/20 border-blue-500 text-white"
                          : "bg-white/[0.02] border-white/10 text-slate-300 hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-white">
                            {cand.full_name || `${cand.first_name || ""} ${cand.last_name || ""}`.trim() || "Profil sans nom"}
                          </p>
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                              isDraft
                                ? "bg-amber-500/10 text-amber-300 border-amber-500/20"
                                : "bg-blue-500/10 text-blue-300 border-blue-500/20"
                            }`}
                          >
                            {isDraft ? "CV Builder" : "Candidat"}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          {cand.title || "Titre non renseigné"} &bull; {cand.email || "Email non renseigné"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isSelected && <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {linkingDraft && (
              <div className="flex items-center justify-center py-2 text-xs text-blue-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                Rattachement immédiat de la fiche candidat...
              </div>
            )}

            {/* Conflict Resolution Modal for Identical Name/Profile */}
            {conflictModal && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-3">
                <div className="flex items-start gap-2">
                  <UserCheck className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-white">
                      Identité correspondante détectée
                    </p>
                    <p className="text-[11px] text-slate-300 mt-0.5">
                      Un candidat existant correspond à cette identité :{" "}
                      <strong className="text-amber-300">{conflictModal.existing_candidate_name}</strong>.
                      Souhaitez-vous conserver et modifier l&apos;existant ou remplacer le CV de référence ?
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setConflictModal(null)}
                    className="px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResolveConflict(false, true)}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-white border border-white/10"
                  >
                    Conserver et modifier l&apos;existant
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResolveConflict(true, false)}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-xs font-semibold text-white shadow"
                  >
                    Remplacer le CV de référence
                  </button>
                </div>
              </div>
            )}

            {selectedExistingCandidate && (
              <div className="pt-2 border-t border-white/10 space-y-3">
                <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
                  Candidat sélectionné : <span className="font-bold text-white">{selectedExistingCandidate.first_name} {selectedExistingCandidate.last_name}</span>
                </div>

                <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                  Action à réaliser
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setOrientation("need")}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      orientation === "need"
                        ? "bg-blue-600/20 border-blue-500 text-white"
                        : "bg-white/[0.02] border-white/10 text-slate-400"
                    }`}
                  >
                    <p className="text-xs font-semibold flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-blue-400" />
                      Positionner sur un besoin
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrientation("pool")}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      orientation === "pool"
                        ? "bg-amber-600/20 border-amber-500 text-white"
                        : "bg-white/[0.02] border-white/10 text-slate-400"
                    }`}
                  >
                    <p className="text-xs font-semibold flex items-center gap-1.5">
                      <Award className="h-3.5 w-3.5 text-amber-400" />
                      Inscrire au vivier
                    </p>
                  </button>
                </div>

                {orientation === "need" && (
                  <select
                    value={selectedNeedId}
                    onChange={(e) => setSelectedNeedId(e.target.value)}
                    required
                    className="w-full rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">-- Choisir une mission client --</option>
                    {availableNeeds.map((n) => (
                      <option key={n._id} value={n._id}>
                        {n.title} {n.client_id ? `(${n.client_id})` : ""}
                      </option>
                    ))}
                  </select>
                )}

                {orientation === "pool" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={poolForm.director_name}
                      onChange={(e) => setPoolForm({ ...poolForm, director_name: e.target.value })}
                      placeholder="Directeur ayant donné son accord *"
                      className="rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                    />
                    <input
                      type="text"
                      value={poolForm.rh_name}
                      onChange={(e) => setPoolForm({ ...poolForm, rh_name: e.target.value })}
                      placeholder="Collaborateur RH validant *"
                      className="rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                    />
                    <input
                      type="text"
                      value={poolForm.reason}
                      onChange={(e) => setPoolForm({ ...poolForm, reason: e.target.value })}
                      placeholder="Motif d'inscription au vivier *"
                      className="sm:col-span-2 rounded-xl bg-slate-900 border border-white/10 px-3 py-2 text-xs text-white"
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white shadow-md"
                  >
                    {submitting ? "Traitement..." : "Confirmer l'action"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DialogContent></Dialog>
  );
}

export default function CandidateCreateModal(props) {
  if (!props.isOpen) return null;
  return (
    <CvBuilderProvider persistLocalDraft={false} resumeImport={false} jobStorageKey="lenvia_candidate_cv_parse_job">
      <CandidateCreateForm {...props} />
    </CvBuilderProvider>
  );
}
